import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import Column, { AutoColumn } from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import FiatValue from 'components/FiatValue'
import Row, { RowBetween, RowTitled } from 'components/Row'
import ConfirmationTransactionStep, {
  ConfirmationTransactionStepProps,
} from 'components/StepLine/common/ConfirmationTransactionStep'
import { useKodiakFarmEarned } from 'hooks/useKodiakFarm'
import { KodiakFarmStake } from 'hooks/useKodiakFarm'
import sumCurrencyAmount from 'lib/utils/sumCurrencyAmount'
import { useContext, useMemo } from 'react'
import { Text } from 'rebass'
import { ThemeContext } from 'styled-components'
import { Separator } from 'theme'
import { KodiakFarm } from 'types/farms'

type WithdrawFromStakeStepProps = {
  stakes: KodiakFarmStake[]
  farm?: KodiakFarm | null
  next: () => void
  onWithdraw: () => void
} & Omit<ConfirmationTransactionStepProps, 'children' | 'onConfirm'>

const StakeView = ({ stake }: { stake: KodiakFarmStake }) => {
  return (
    <RowTitled
      reverse
      header={
        <Text fontSize="14px" style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
          {stake.liquidity?.toSignificant(6)} {stake.liquidity?.currency?.symbol}
        </Text>
      }
    />
  )
}
const WithdrawFromStakeStep = ({ stakes, onWithdraw, next, farm, ...rest }: WithdrawFromStakeStepProps) => {
  const sum = useMemo(() => {
    return sumCurrencyAmount(stakes.map((item) => item.liquidity))
  }, [stakes])

  const theme = useContext(ThemeContext)
  const earned = useKodiakFarmEarned(farm)

  return (
    <ConfirmationTransactionStep {...rest} onConfirm={next}>
      <Column width="100%" gap="8px">
        <LightCard>
          <AutoColumn gap="8px">
            {stakes.map((stake) => {
              return <StakeView stake={stake} key={stake.kekId} />
            })}
            <Separator />

            <RowBetween>
              <Row grow="1">
                <Text fontSize={14} color={theme.textSecondary}>
                  Total
                </Text>
              </Row>
              <Row
                grow="0"
                shrink="0"
                width="fit-content"
                style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
                justify="flex-end"
              >
                <Text fontSize={14} marginRight="4px" color={theme.textSecondary}>
                  (<FiatValue currencyAmount={sum} />)
                </Text>
                <Text fontSize={14}>
                  {sum?.toSignificant(6)} {sum?.currency?.symbol}
                </Text>
              </Row>
            </RowBetween>
          </AutoColumn>
        </LightCard>
        <LightCard>
          <AutoColumn gap="8px">
            <Text fontSize={14} color={theme.textSecondary}>
              You will also unlock the following tokens as rewards
            </Text>
            {earned?.map((e) => {
              return (
                <RowBetween key={e.currency.address}>
                  <Row grow="0" shrink="0" width="fit-content">
                    <CurrencyLogo currency={e.currency} size="21px" style={{ marginRight: '8px' }} />
                    <Text fontSize={14}>{e.currency.symbol}</Text>
                  </Row>
                  <Row grow="1" style={{ whiteSpace: 'nowrap', overflow: 'hidden' }} justify="flex-end">
                    <Text fontSize={14} marginRight="4px" color={theme.textSecondary}>
                      (<FiatValue currencyAmount={e} />)
                    </Text>
                    <Text fontSize={14}>{e.toSignificant(6)}</Text>
                  </Row>
                </RowBetween>
              )
            })}
          </AutoColumn>
        </LightCard>
        <ButtonPrimary onClick={onWithdraw}>
          Withdraw {sum?.toSignificant(6)} {sum?.currency?.symbol}
        </ButtonPrimary>
      </Column>
    </ConfirmationTransactionStep>
  )
}

export default WithdrawFromStakeStep
